import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell,
} from "recharts";
import api from "../lib/api";

const COLORS_NETWORK = ["#FB923C", "#0F172A", "#3F3F46", "#65A30D", "#0EA5E9", "#A855F7"];
const COLORS_CURRENCY = { CAD: "#FB923C", USD: "#0F172A" };

function _fmtMoney(n, ccy) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency", currency: ccy || "CAD",
      minimumFractionDigits: 0, maximumFractionDigits: 2,
    }).format(n || 0);
  } catch (_e) {
    return `${(n || 0).toFixed(2)} ${ccy || ""}`.trim();
  }
}

function _fmtQty(n, uom) {
  const num = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0, maximumFractionDigits: 1,
  }).format(n || 0);
  if (uom === "L") return `${num} L`;
  if (uom === "G") return `${num} gal`;
  return `${num} ${uom || ""}`.trim();
}

function _today() {
  return new Date().toISOString().slice(0, 10);
}

function _twelveMonthsAgo() {
  const d = new Date();
  d.setMonth(d.getMonth() - 11);
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}

function ChartCard({ title, subtitle, testId, children, right }) {
  return (
    <div className="gf-card" data-testid={testId}>
      <div className="gf-card-header flex items-center justify-between">
        <div>
          <h3>{title}</h3>
          {subtitle && <div className="text-xs text-zinc-400 mt-1">{subtitle}</div>}
        </div>
        {right}
      </div>
      <div className="gf-card-body">{children}</div>
    </div>
  );
}

function _pivotMonthly(rows, valueKey, keyKey) {
  // [{month, [keyKey]: K, [valueKey]: V}] → [{month, K1: V1, K2: V2}]
  const out = {};
  const keys = new Set();
  for (const r of rows) {
    const k = r[keyKey];
    keys.add(k);
    out[r.month] = out[r.month] || { month: r.month };
    out[r.month][k] = (out[r.month][k] || 0) + (r[valueKey] || 0);
  }
  return { data: Object.values(out).sort((a, b) => a.month.localeCompare(b.month)),
           keys: Array.from(keys) };
}

export default function CustomerDashboardCharts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [from, setFrom] = useState(_twelveMonthsAgo());
  const [to, setTo] = useState(_today());

  useEffect(() => {
    setLoading(true);
    api.get("/portal/dashboard/charts", { params: { date_from: from, date_to: to } })
      .then(({ data }) => { setData(data); setErr(null); })
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load charts."))
      .finally(() => setLoading(false));
  }, [from, to]);

  const monthlySpendPivot = useMemo(
    () => _pivotMonthly(data?.monthly_spend || [], "amount", "currency"),
    [data?.monthly_spend],
  );
  const monthlyVolumePivot = useMemo(
    () => _pivotMonthly(
      data?.monthly_volume || [],
      "qty",
      // pivot key combines country+uom so we get e.g. "CA (L)" / "US (gal)".
      "country",
    ),
    [data?.monthly_volume],
  );

  if (loading && !data) {
    return (
      <div
        className="gf-card mt-6"
        data-testid="customer-charts-loading"
      >
        <div className="gf-card-body text-center text-zinc-400 py-10">
          Loading charts…
        </div>
      </div>
    );
  }
  if (err) {
    return (
      <div className="gf-card mt-6">
        <div
          className="gf-card-body text-sm text-rose-600"
          data-testid="customer-charts-error"
        >
          {err}
        </div>
      </div>
    );
  }
  if (!data) return null;

  const cadTotal = data.totals_by_currency?.CAD || 0;
  const usdTotal = data.totals_by_currency?.USD || 0;
  const hasUSD = (data.country_spend || []).some((r) => r.currency === "USD");
  const hasCAD = (data.country_spend || []).some((r) => r.currency === "CAD");

  return (
    <div className="space-y-6 mt-6" data-testid="customer-charts">
      <div className="gf-card">
        <div className="gf-card-body flex flex-wrap items-end gap-4">
          <div>
            <div className="overline text-[10px]">FROM</div>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="gf-input"
              data-testid="customer-charts-from"
            />
          </div>
          <div>
            <div className="overline text-[10px]">TO</div>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="gf-input"
              data-testid="customer-charts-to"
            />
          </div>
          <div className="flex items-end gap-6 ml-auto">
            {hasCAD && (
              <div className="text-right" data-testid="customer-charts-total-cad">
                <div className="overline text-[10px]">TOTAL · CAD</div>
                <div className="text-xl font-mono">{_fmtMoney(cadTotal, "CAD")}</div>
              </div>
            )}
            {hasUSD && (
              <div className="text-right" data-testid="customer-charts-total-usd">
                <div className="overline text-[10px]">TOTAL · USD</div>
                <div className="text-xl font-mono">{_fmtMoney(usdTotal, "USD")}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 1. Monthly fuel spend ------------------------------------------- */}
      <ChartCard
        title="Monthly fuel spend"
        subtitle="CAD and USD are shown side-by-side and never combined."
        testId="customer-chart-monthly-spend"
      >
        {monthlySpendPivot.data.length === 0 ? (
          <div className="text-sm text-zinc-400 text-center py-8">
            No spend in this window.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={monthlySpendPivot.data}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e4e4e7" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(v, name) => [_fmtMoney(v, name), name]}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {monthlySpendPivot.keys.map((ccy) => (
                <Bar key={ccy} dataKey={ccy} fill={COLORS_CURRENCY[ccy] || "#71717A"} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      {/* 2. Monthly volume ----------------------------------------------- */}
      <ChartCard
        title="Monthly volume"
        subtitle="Canada in litres · USA in gallons. Volumes are not summed across UOMs."
        testId="customer-chart-monthly-volume"
      >
        {monthlyVolumePivot.data.length === 0 ? (
          <div className="text-sm text-zinc-400 text-center py-8">
            No fueling in this window.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={monthlyVolumePivot.data}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e4e4e7" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis
                tick={{ fontSize: 11 }}
                tickFormatter={(v) =>
                  new Intl.NumberFormat(undefined, { notation: "compact" }).format(v)
                }
              />
              <Tooltip
                formatter={(v, name) => {
                  const uom = name === "CA" ? "L" : "G";
                  return [_fmtQty(v, uom), name === "CA" ? "Canada (L)" : "USA (gal)"];
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: 12 }}
                formatter={(value) => (value === "CA" ? "Canada (L)" : "USA (gal)")}
              />
              {monthlyVolumePivot.keys.includes("CA") && (
                <Bar dataKey="CA" fill="#FB923C" />
              )}
              {monthlyVolumePivot.keys.includes("US") && (
                <Bar dataKey="US" fill="#0F172A" />
              )}
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 3. Spend by network ----------------------------------------- */}
        <ChartCard
          title="Spend by network"
          subtitle="One slice per network · currency-aware totals."
          testId="customer-chart-network-spend"
        >
          {(data.spend_by_network || []).length === 0 ? (
            <div className="text-sm text-zinc-400 text-center py-8">
              No spend in this window.
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={data.spend_by_network}
                    dataKey="amount"
                    nameKey="network"
                    cx="50%" cy="50%" outerRadius={80}
                    paddingAngle={1}
                  >
                    {(data.spend_by_network || []).map((r, i) => (
                      <Cell key={i} fill={COLORS_NETWORK[i % COLORS_NETWORK.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v, _n, props) =>
                      [_fmtMoney(v, props?.payload?.currency || "CAD"),
                       (props?.payload?.network || "").toUpperCase()]}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="text-xs space-y-1 mt-2" data-testid="network-spend-list">
                {data.spend_by_network.map((r, i) => (
                  <div key={i} className="flex justify-between font-mono">
                    <span>
                      <span
                        className="inline-block w-2 h-2 rounded-sm mr-2"
                        style={{ background: COLORS_NETWORK[i % COLORS_NETWORK.length] }}
                      />
                      {(r.network || "").toUpperCase()}
                    </span>
                    <span>{_fmtMoney(r.amount, r.currency)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </ChartCard>

        {/* 4. Canada vs USA spend ------------------------------------- */}
        <ChartCard
          title="Canada vs USA spend"
          subtitle="CAD and USD reported separately."
          testId="customer-chart-country-spend"
        >
          {(data.country_spend || []).length === 0 ? (
            <div className="text-sm text-zinc-400 text-center py-8">
              No spend in this window.
            </div>
          ) : (
            <div className="space-y-3" data-testid="country-spend-rows">
              {data.country_spend.map((r) => (
                <div
                  key={`${r.country}-${r.currency}`}
                  className="border border-zinc-100 rounded p-3"
                  data-testid={`country-spend-${r.country}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-medium">
                      {r.country === "CA" ? "Canada" : "USA"}
                    </div>
                    <div className="text-xs text-zinc-400 font-mono">
                      {r.currency} · {r.uom === "L" ? "Litres" : "Gallons"}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="overline text-[10px]">SPEND</div>
                      <div className="font-mono text-zinc-900">
                        {_fmtMoney(r.amount, r.currency)}
                      </div>
                    </div>
                    <div>
                      <div className="overline text-[10px]">VOLUME</div>
                      <div className="font-mono text-zinc-900">
                        {_fmtQty(r.qty, r.uom)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ChartCard>
      </div>

      {/* 5. Spend by card / unit -------------------------------------- */}
      <ChartCard
        title="Spend by card / unit"
        subtitle={`Top ${data.spend_by_card?.length || 0} of ${data.spend_by_card_total_rows || 0} cards.`}
        testId="customer-chart-card-spend"
      >
        {(data.spend_by_card || []).length === 0 ? (
          <div className="text-sm text-zinc-400 text-center py-8">
            No card activity in this window.
          </div>
        ) : (
          <table className="gf-table" data-testid="card-spend-table">
            <thead>
              <tr>
                <th>CARD</th>
                <th>UNIT #</th>
                <th>DRIVER</th>
                <th>NETWORK</th>
                <th>VOLUME</th>
                <th className="text-right">SPEND</th>
              </tr>
            </thead>
            <tbody>
              {data.spend_by_card.map((r, i) => (
                <tr key={`${r.card_last5}-${r.network}-${r.currency}-${i}`}>
                  <td className="font-mono text-xs">····{r.card_last5}</td>
                  <td className="font-mono text-xs">{r.unit || "—"}</td>
                  <td className="text-xs">{r.driver || "—"}</td>
                  <td className="overline text-[10px]">{(r.network || "").toUpperCase()}</td>
                  <td className="font-mono text-xs">{_fmtQty(r.qty, r.uom)}</td>
                  <td className="font-mono text-right">{_fmtMoney(r.amount, r.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </ChartCard>
    </div>
  );
}
